import { createContext, type RefObject, useRef, useState } from 'react';

import type { CardLayer } from './_components/DigitalCardLayer/types';
import { useAllResourceLoad } from './_hooks/useAllResourceLoad';
import { useDigitalCardStyles } from './_hooks/useDigitalCardStyles';
import useFlipActions from './_hooks/useFlipActions';
import { useTiltActions } from './_hooks/useTiltActions';

export const DEFAULT_MAX_WIDTH = 300;
export const DEFAULT_IMAGE_LOAD_RETRY_COUNT = 0;
export const DEFAULT_IMAGE_LOAD_DELAY_TIME = 0;
export const DEFAULT_VIDEO_LOAD_RETRY_COUNT = 0;
export const DEFAULT_VIDEO_LOAD_DELAY_TIME = 0;
export const DEFAULT_SPRITE_IMAGE_LOAD_RETRY_COUNT = 0;
export const DEFAULT_SPRITE_IMAGE_LOAD_DELAY_TIME = 0;

/**
 * [디지털 카드 면 인터페이스]
 *
 * @description
 * 디지털 카드의 한 면을 구성하는 인터페이스입니다.
 * 각 면은 여러 레이어로 구성되며, 각 레이어는 이미지, 컴포넌트, 효과 등을 포함할 수 있습니다.
 */
export interface DigitalCardFace {
  idx: number;
  layers: CardLayer[];
  borderRadius: number;
}

/**
 * [디지털 카드 설정 인터페이스]
 *
 * @description
 * 디지털 카드의 기본 설정을 정의하는 인터페이스입니다.
 * 카드의 크기, 면 구성, 기능 활성화 여부 등을 설정할 수 있습니다.
 */
export interface DigitalCardConfig {
  faces: DigitalCardFace[];
  width: number;
  height: number;
  flip?: boolean;
  tilt?: boolean;
  maxWidth?: number;
  imageLoadRetryCount?: number;
  imageLoadDelayTime?: number;
  videoLoadRetryCount?: number;
  videoLoadDelayTime?: number;
  spriteImageLoadRetryCount?: number;
  spriteImageLoadDelayTime?: number;
}

/**
 * [디지털 카드 상태 인터페이스]
 *
 * @description
 * 디지털 카드의 현재 상태를 나타내는 인터페이스입니다.
 * 상호작용 상태와 리소스 로딩 상태를 포함합니다.
 */
export interface DigitalCardState {
  currentFaceIdx: number;
  isInteracting: boolean;
  cardRef: RefObject<HTMLDivElement | null> | null;
  visibleFaces: Array<{
    face: DigitalCardFace;
    position: 'prev' | 'current' | 'next';
  }>;
  isAllResourcesLoaded: boolean;
  isAllResourceLoadError: boolean;
  completedResources: Set<string>;
}

/**
 * [디지털 카드 액션 인터페이스]
 *
 * @description
 * 디지털 카드의 사용자 상호작용과 리소스 로딩을 처리하는 액션들을 정의합니다.
 */
export interface DigitalCardActions {
  handleFlipToNext: () => void;
  handleFlipToPrev: () => void;
  onMouseEnter: (e: React.MouseEvent | React.TouchEvent) => void;
  onMouseLeave: () => void;
  onMouseMove: (e: React.MouseEvent | React.TouchEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  handleResourceLoadComplete: (src: string) => void;
  handleResourceLoadError: () => void;
}

/**
 * [디지털 카드 프로바이더 속성 인터페이스]
 *
 * @description
 * DigitalCardProvider 컴포넌트의 props를 정의하는 인터페이스입니다.
 * DigitalCardConfig의 모든 속성과 children을 포함합니다.
 */
export interface DigitalCardProviderProps extends DigitalCardConfig {
  children: React.ReactNode;
}

/**
 * [디지털 카드 컨텍스트]
 *
 * @description
 * 디지털 카드의 설정, 상태, 액션을 관리하는 Context 객체들입니다.
 */
export const DigitalCardConfigContext = createContext<DigitalCardConfig | undefined>(undefined);
export const DigitalCardStateContext = createContext<DigitalCardState | undefined>(undefined);
export const DigitalCardActionsContext = createContext<DigitalCardActions | undefined>(undefined);

/**
 * [디지털 카드 프로바이더]
 *
 * @description
 * 디지털 카드의 상태 관리를 위한 Context Provider 컴포넌트입니다.
 * 설정(Config), 상태(State), 액션(Actions)의 세 가지 컨텍스트를 제공합니다.
 *
 * @param props
 * ```ts
 * {
 *   children: React.ReactNode,           // 자식 컴포넌트
 *   faces: DigitalCardFace[],           // 카드의 면 구성
 *   width: number,                      // 카드 너비
 *   height: number,                     // 카드 높이
 *   flip?: boolean,                     // 플립 기능 활성화 (기본값: true)
 *   tilt?: boolean                      // 틸트 기능 활성화 (기본값: true)
 * }
 * ```
 *
 * @context
 * 1. ConfigContext
 * ```ts
 * {
 *   faces: DigitalCardFace[],           // 카드 면 구성
 *   width: number,                      // 카드 크기
 *   height: number,
 *   flip: boolean,                      // 플립 기능 활성화 상태
 *   tilt: boolean,                      // 틸트 기능 활성화 상태
 *   maxWidth: number,                   // 카드 최대 너비
 *   imageLoadRetryCount: number,        // 이미지 로딩 재시도 횟수
 *   imageLoadDelayTime: number,         // 이미지 로딩 딜레이 시간
 *   videoLoadRetryCount: number,        // 비디오 로딩 재시도 횟수
 *   videoLoadDelayTime: number,         // 비디오 로딩 딜레이 시간
 *   spriteImageLoadRetryCount: number,  // 스프라이트 이미지 로딩 재시도 횟수
 *   spriteImageLoadDelayTime: number,   // 스프라이트 이미지 로딩 딜레이 시간
 * }
 * ```
 *
 * @see {@link useDigitalCardConfig} 설정 접근 훅
 * @see {@link useDigitalCardState} 상태 접근 훅
 * @see {@link useDigitalCardActions} 액션 접근 훅
 */
export const DigitalCardProvider = ({
  children,
  faces,
  width,
  height,
  maxWidth = DEFAULT_MAX_WIDTH,
  flip: flip = true,
  tilt: tilt = true,
  imageLoadRetryCount = DEFAULT_IMAGE_LOAD_RETRY_COUNT,
  imageLoadDelayTime = DEFAULT_IMAGE_LOAD_DELAY_TIME,
  videoLoadRetryCount = DEFAULT_VIDEO_LOAD_RETRY_COUNT,
  videoLoadDelayTime = DEFAULT_VIDEO_LOAD_DELAY_TIME,
  spriteImageLoadRetryCount = DEFAULT_SPRITE_IMAGE_LOAD_RETRY_COUNT,
  spriteImageLoadDelayTime = DEFAULT_SPRITE_IMAGE_LOAD_DELAY_TIME,
}: DigitalCardProviderProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // 상호작용 상태
  const [currentFaceIdx, setCurrentFaceIdx] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);

  // Flip 관련 액션과 상태
  const { visibleFaces, handleFlipToNext, handleFlipToPrev } = useFlipActions({
    faces,
    currentFaceIdx,
    setCurrentFaceIdx,
    isEnabled: flip,
  });

  // Tilt 관련 액션과 상태
  const { handlers: tiltHandlers } = useTiltActions({
    cardRef,
    setIsInteracting,
    isEnabled: tilt,
  });

  // CSS 로딩 관련 상태
  const { isStylesLoaded } = useDigitalCardStyles();

  // 리소스 로딩 관련 상태와 액션
  // CSS 로딩 상태를 전달하여 CSS + 리소스 모두 로드될 때까지 대기
  const {
    isAllResourcesLoaded,
    isAllResourceLoadError,
    completedResources,
    handleResourceLoadComplete,
    handleResourceLoadError,
  } = useAllResourceLoad(visibleFaces, isStylesLoaded);

  // 설정값 (거의 변경되지 않는 값들)
  const config: DigitalCardConfig = {
    faces,
    width,
    height,
    flip,
    tilt,
    maxWidth,
    imageLoadRetryCount,
    imageLoadDelayTime,
    videoLoadRetryCount,
    videoLoadDelayTime,
    spriteImageLoadRetryCount,
    spriteImageLoadDelayTime,
  };

  // 상태값
  const state: DigitalCardState = {
    currentFaceIdx,
    isInteracting,
    cardRef,
    visibleFaces,
    isAllResourcesLoaded,
    isAllResourceLoadError,
    completedResources,
  };

  // 액션값
  const actions: DigitalCardActions = {
    handleFlipToNext,
    handleFlipToPrev,
    onMouseEnter: tiltHandlers.onMouseEnter,
    onMouseLeave: tiltHandlers.onMouseLeave,
    onMouseMove: tiltHandlers.onMouseMove,
    onTouchStart: tiltHandlers.onTouchStart,
    onTouchMove: tiltHandlers.onTouchMove,
    onTouchEnd: tiltHandlers.onTouchEnd,
    handleResourceLoadComplete,
    handleResourceLoadError,
  };

  return (
    <DigitalCardConfigContext.Provider value={config}>
      <DigitalCardStateContext.Provider value={state}>
        <DigitalCardActionsContext.Provider value={actions}>{children}</DigitalCardActionsContext.Provider>
      </DigitalCardStateContext.Provider>
    </DigitalCardConfigContext.Provider>
  );
};
